import React, { useState, useEffect } from "react";
import styles from "./StatisticsDisplay.module.scss";

const StatisticsDisplay = ({ statistics }) => {
  const [displayedStats, setDisplayedStats] = useState({
    total_gmv: 0,
    gmv_per_product: 0,
    total_products: 0,
    total_sells: 0,
    top_brands: [],
    top_categories: [],
  });

  const animateNumber = (start, end, duration, callback) => {
    const startTime = performance.now();
    const difference = end - start;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = start + difference * easeOutQuart;

      callback(Math.floor(current));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (statistics) {
      const numericKeys = [
        "total_gmv",
        "gmv_per_product",
        "total_products",
        "total_sells",
      ];
      const delays = [0, 200, 400, 600];

      numericKeys.forEach((key, index) => {
        if (statistics[key] !== undefined) {
          setTimeout(() => {
            animateNumber(0, statistics[key], 2000, (value) => {
              setDisplayedStats((prev) => ({
                ...prev,
                [key]: value,
              }));
            });
          }, delays[index]);
        }
      });

      // Set non-numeric data immediately
      setDisplayedStats((prev) => ({
        ...prev,
        top_brands: statistics.top_brands || [],
        top_categories: statistics.top_categories || [],
      }));
    }
  }, [statistics]);

  const formatCurrency = (num, currency = "KZT") => {
    if (!currency || currency === "") {
      return new Intl.NumberFormat("ru-RU").format(num);
    }

    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const statsConfig = [
    {
      key: "total_gmv",
      title: "Общая выручка",
      value: formatCurrency(displayedStats.total_gmv),
      icon: "💰",
      color: "primary",
    },
    {
      key: "gmv_per_product",
      title: "Выручка на товар",
      value: formatCurrency(displayedStats.gmv_per_product),
      icon: "📦",
      color: "success",
    },
    {
      key: "total_products",
      title: "Всего товаров",
      value: formatCurrency(displayedStats.total_products, ""),
      icon: "📦",
      color: "info",
    },
    {
      key: "total_sells",
      title: "Всего продаж",
      value: formatCurrency(displayedStats.total_sells, ""),
      icon: "📈",
      color: "warning",
    },
  ];

  return (
    <div>
      {/* Main Statistics Cards */}
      <div className="row mb-5">
        {statsConfig.map((stat, index) => (
          <div key={stat.key} className="col-lg-3 col-md-6 col-sm-12 mb-4">
            <div className={`card border-0 shadow-sm h-100 ${styles.statCard}`}>
              <div className="card-body text-center p-4">
                <div className={`${styles.statIcon} mb-3`}>
                  <span className="fs-1">{stat.icon}</span>
                </div>
                <h3 className={`${styles.statValue} mb-2 text-${stat.color}`}>
                  {stat.value}
                </h3>
                <h6 className={`${styles.statTitle} text-muted mb-0`}>
                  {stat.title}
                </h6>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top Brands and Categories Section */}
      <div className="row">
        {/* Top Brands */}
        <div className="col-lg-6 col-md-12 mb-4">
          <div
            className={`card border-0 shadow-sm h-100 ${styles.topSectionCard}`}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-center mb-3">
                <div className={`${styles.sectionIcon} me-3`}>
                  <span className="fs-3">🏆</span>
                </div>
                <h5 className={`${styles.sectionTitle} mb-0`}>Топ бренды</h5>
              </div>
              <div className={styles.listContainer}>
                {displayedStats.top_brands &&
                displayedStats.top_brands.length > 0 ? (
                  displayedStats.top_brands.map((brandItem, index) => {
                    // Handle both string and object formats
                    const brandName =
                      typeof brandItem === "string"
                        ? brandItem
                        : brandItem.brand;
                    const brandStats =
                      typeof brandItem === "object" ? brandItem : null;

                    return (
                      <div
                        key={index}
                        className={`${styles.listItem} ${styles.brandItem}`}
                      >
                        <div className={styles.rankBadge}>
                          <span className={styles.rankNumber}>{index + 1}</span>
                        </div>
                        <div className={styles.itemContent}>
                          <span className={styles.itemName}>{brandName}</span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyState}>
                    <span className="text-muted">Нет данных о брендах</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top Categories */}
        <div className="col-lg-6 col-md-12 mb-4">
          <div
            className={`card border-0 shadow-sm h-100 ${styles.topSectionCard}`}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-center mb-3">
                <div className={`${styles.sectionIcon} me-3`}>
                  <span className="fs-3">📂</span>
                </div>
                <h5 className={`${styles.sectionTitle} mb-0`}>Топ категории</h5>
              </div>
              <div className={styles.listContainer}>
                {displayedStats.top_categories &&
                displayedStats.top_categories.length > 0 ? (
                  displayedStats.top_categories.map((categoryItem, index) => {
                    // Handle both string and object formats
                    const categoryName =
                      typeof categoryItem === "string"
                        ? categoryItem
                        : categoryItem.category;
                    const categoryStats =
                      typeof categoryItem === "object" ? categoryItem : null;

                    return (
                      <div
                        key={index}
                        className={`${styles.listItem} ${styles.categoryItem}`}
                      >
                        <div className={styles.rankBadge}>
                          <span className={styles.rankNumber}>{index + 1}</span>
                        </div>
                        <div className={styles.itemContent}>
                          <span className={styles.itemName}>
                            {categoryName}
                          </span>
                          {categoryStats && (
                            <div className={styles.itemStats}>
                              <small className="text-muted">
                                GMV:{" "}
                                {formatCurrency(categoryStats.total_gmv || 0)} |
                                Товаров:{" "}
                                {/* {formatNumber(categoryStats.product_count || 0)} */}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyState}>
                    <span className="text-muted">Нет данных о категориях</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDisplay;
